<div class="staff-shell">

  <!-- ════════════════════════════════════
       TOP HEADER BAR
  ════════════════════════════════════ -->
  <header class="topbar">
    <!-- Brand / Shop name -->
    <div class="topbar__brand">
      <div class="topbar__logo-ring">
        <mat-icon class="topbar__logo-icon">store</mat-icon>
      </div>
      <div class="topbar__brand-info">
        <span class="topbar__shop-name">{{ shopName || 'POS System' }}</span>
        <span class="topbar__status">
          <span class="topbar__status-dot"></span>{{ 'Online' | translate }}
        </span>
      </div>
    </div>

    <!-- Right: user info -->
    <div class="topbar__user">
      <div class="topbar__user-text">
        <span class="topbar__greeting">{{ 'Welcome back' | translate }}</span>
        <span class="topbar__username">{{ currentUser || 'Admin' }}</span>
      </div>
      <button mat-icon-button class="topbar__avatar-btn" [matTooltip]="'Profile' | translate">
        <mat-icon>account_circle</mat-icon>
      </button>
    </div>
  </header>

  <!-- ════════════════════════════════════
       BODY: icon rail + sub-menu + content
  ════════════════════════════════════ -->
  <div class="staff-body">

    <!-- ── ICON RAIL ────────────────── -->
    <nav class="icon-rail" aria-label="Main navigation">
      <!-- Ambient glow blob -->
      <div class="rail-glow"></div>

      @for (menuItem of mainMenu; track menuItem) {
        <a
          *ngxPermissionsOnly="menuItem.permissions?.length ? menuItem.permissions : undefined"
          class="rail-item"
          [routerLink]="menuItem.route"
          routerLinkActive="rail-item--active"
          [matTooltip]="menuItem.name | translate"
          matTooltipPosition="right"
          matTooltipShowDelay="600">

          <!-- Active side bar -->
          <span class="rail-item__bar"></span>

          <!-- Icon wrapper -->
          <span class="rail-item__icon-wrap">
            @if (menuItem.logo) {
              <img [src]="menuItem.logo"
                   [alt]="menuItem.name"
                   class="rail-item__img" />
            } @else {
              @if (menuItem.iconType === 'MATERIAL') {
                <mat-icon class="rail-item__mat-icon">{{ menuItem.icon }}</mat-icon>
              }
              @if (menuItem.iconType === 'SVG') {
                <mat-icon [svgIcon]="menuItem.icon" class="rail-item__mat-icon"></mat-icon>
              }
            }
          </span>

          <!-- Ripple hover glow -->
          <span class="rail-item__glow"></span>
        </a>
      }

      <!-- Spacer pushes logout to bottom -->
      <div class="rail-spacer"></div>

      <!-- Divider -->
      <div class="rail-divider"></div>

      <!-- Logout button -->
      <button
        class="rail-logout"
        (click)="logout()"
        [matTooltip]="'Logout' | translate"
        matTooltipPosition="right">
        <span class="rail-item__icon-wrap rail-logout__icon-wrap">
          <mat-icon class="rail-logout__icon">logout</mat-icon>
        </span>
        <span class="rail-item__glow"></span>
      </button>
    </nav>

    <!-- ── SUB-MENU PANEL ────────────── -->
    <aside class="sub-panel">
      <!-- Panel background decoration -->
      <div class="sub-panel__deco"></div>

      <!-- Module header -->
      <div class="module-header">
        <div class="module-header__icon-wrap">
          @if (activeModule?.iconType === 'MATERIAL') {
            <mat-icon class="module-header__icon">{{ activeModule?.icon }}</mat-icon>
          } @else {
            <mat-icon class="module-header__icon">category</mat-icon>
          }
        </div>
        <div class="module-header__info">
          <h2 class="module-header__title">{{ activeModuleName | translate }}</h2>
          @if (activeModule?.description) {
            <p class="module-header__desc">{{ activeModule?.description | translate }}</p>
          }
        </div>
      </div>

      <!-- Divider -->
      <div class="panel-divider"></div>

      <!-- Nav list -->
      <div class="panel-nav">
        <mat-nav-list class="panel-nav-list">
          @for (menu of menuData; track menu) {

            <!-- Section hint header -->
            @if (menu?.isHint) {
              <div class="section-header">
                <span class="section-header__line"></span>
                <span class="section-header__label">{{ menu?.name | translate }}</span>
                <span class="section-header__line"></span>
              </div>
            }

            <!-- Single menu item -->
            @if (!menu.children && !menu?.isHint) {
              <a [routerLink]="menu.route"
                 routerLinkActive="menu-item--active"
                 class="menu-item">

                <!-- Active indicator -->
                <span class="menu-item__bar"></span>

                <!-- Icon -->
                @if (menu.icon) {
                  <span class="menu-item__icon-wrap">
                    @if (menu.iconType == 'SVG') {
                      <mat-icon [svgIcon]="menu.icon" class="menu-item__icon"></mat-icon>
                    }
                    @if (menu.iconType == 'MATERIAL') {
                      <mat-icon class="menu-item__icon">{{ menu.icon }}</mat-icon>
                    }
                  </span>
                }

                <span class="menu-item__label">{{ menu.name | translate }}</span>

                <span class="menu-item__chevron">
                  <mat-icon class="chevron-right-icon">chevron_right</mat-icon>
                </span>
              </a>
            }

            <!-- Expandable menu item -->
            @if (menu?.children) {
              <mat-accordion class="accordion">
                <mat-expansion-panel
                  class="expansion-panel"
                  [expanded]="isActiveRoute(menu?.children)"
                  hideToggle>

                  <mat-expansion-panel-header class="expansion-header">
                    <mat-panel-title class="expansion-title">
                      @if (menu.icon) {
                        <span class="menu-item__icon-wrap">
                          @if (menu.iconType == 'SVG') {
                            <mat-icon [svgIcon]="menu.icon" class="menu-item__icon"></mat-icon>
                          }
                          @if (menu.iconType == 'MATERIAL') {
                            <mat-icon class="menu-item__icon">{{ menu.icon }}</mat-icon>
                          }
                        </span>
                      }
                      <span class="menu-item__label">{{ menu?.name | translate }}</span>
                      <span class="expansion-chevron">
                        <mat-icon class="expand-icon">expand_more</mat-icon>
                      </span>
                    </mat-panel-title>
                  </mat-expansion-panel-header>

                  <!-- Children -->
                  <div class="children-wrap">
                    <div class="children-track"></div>
                    @for (menuItemChild of menu?.children; track menuItemChild) {
                      <a [routerLink]="menuItemChild.route"
                         routerLinkActive="child-item--active"
                         class="child-item">
                        <span class="child-item__dot"></span>
                        @if (menuItemChild?.icon && menuItemChild.iconType == 'MATERIAL') {
                          <mat-icon class="child-item__icon">{{ menuItemChild.icon }}</mat-icon>
                        }
                        <span class="child-item__label">{{ menuItemChild.name | translate }}</span>
                      </a>
                    }
                  </div>

                </mat-expansion-panel>
              </mat-accordion>
            }
          }
        </mat-nav-list>
      </div>
    </aside>

    <!-- ── MAIN CONTENT placeholder (router-outlet is in parent layout) ──-->
    <!-- Content rendered by staff-layout.component.html -->

  </div><!-- /staff-body -->
</div><!-- /staff-shell -->
